import { useCallback, useEffect } from 'react'
import { StepResponseMap } from './types'
import api from '../../services/api'

interface FileSetters {
  prescription: React.Dispatch<React.SetStateAction<File[]>>
  pill_prescription: React.Dispatch<React.SetStateAction<File[]>>
  address: React.Dispatch<React.SetStateAction<File[]>>
  identity: React.Dispatch<React.SetStateAction<File[]>>
  affiliation: React.Dispatch<React.SetStateAction<File[]>>
  anvisa: React.Dispatch<React.SetStateAction<File[]>>
}

export const useFileClearManager = (fileSetters: Partial<FileSetters>, orderId: string) => {
  const clearRejectedDocuments = useCallback(async () => {
    if (!orderId) return

    try {
      const response = await api.get(`/order/${orderId}/documents`)
      if (!response.data?.success) return

      const documents = response.data.documents || []
      const recipes = response.data.recipies || []

      for (const recipe of recipes) {
        if (recipe?.status && ['denied'].includes(recipe.status)) {
          try {
            await api.delete(`/recipe/excluded/${recipe.id}`)
          } catch (error) {
            console.error('Erro ao remover receita reprovada:', error)
          }
        }
      }

      const flatDocuments = documents.flat()
      for (const doc of flatDocuments) {
        if (doc?.status && ['denied'].includes(doc.status)) {
          try {
            await api.delete(`/document/excluded/${doc.id}`)
          } catch (error) {
            console.error('Erro ao remover documento reprovado:', error)
          }
        }
      }
    } catch (error) {
      console.error('Erro ao limpar documentos rejeitados:', error)
    }
  }, [orderId])

  const clearFiles = useCallback((stepName: keyof StepResponseMap, delay = 1500) => {
    setTimeout(() => {
      const setterMap: Record<keyof StepResponseMap, React.Dispatch<React.SetStateAction<File[]>> | undefined> = {
        prescription: fileSetters.prescription,
        pill_prescription: fileSetters.pill_prescription,
        address: fileSetters.address,
        identity: fileSetters.identity,
        affiliation: fileSetters.affiliation,
        anvisa: fileSetters.anvisa,
        powerOfAttorney: undefined,
        responsibleIdentity: undefined
      }

      const setter = setterMap[stepName]
      if (setter) {
        setter([])
      }
    }, delay)
  }, [fileSetters])

  const clearFileImmediate = useCallback((stepName: keyof StepResponseMap) => {
    const setterMap: Record<keyof StepResponseMap, React.Dispatch<React.SetStateAction<File[]>> | undefined> = {
      prescription: fileSetters.prescription,
      pill_prescription: fileSetters.pill_prescription,
      address: fileSetters.address,
      identity: fileSetters.identity,
      affiliation: fileSetters.affiliation,
      anvisa: fileSetters.anvisa,
      powerOfAttorney: undefined,
      responsibleIdentity: undefined
    }

    const setter = setterMap[stepName]
    if (setter) {
      setter([])
    }
  }, [fileSetters])

  const clearAllFiles = useCallback((delay = 0) => {
    const clearAction = () => {
      Object.values(fileSetters).forEach(setter => {
        if (setter) {
          setter([])
        }
      })
    }

    if (delay > 0) {
      setTimeout(clearAction, delay)
    } else {
      clearAction()
    }
  }, [fileSetters])

  useEffect(() => {
    if (orderId) {
      clearRejectedDocuments()
    }
  }, [orderId, clearRejectedDocuments])

  return {
    clearFiles,
    clearFileImmediate,
    clearAllFiles,
    clearRejectedDocuments
  }
}

export const createProgressManager = () => {
  const simulateProgress = (
    onProgress: (progress: number) => void,
    onComplete?: () => void,
    duration = 2000
  ) => {
    let progress = 0
    const increment = 10
    const intervalTime = duration / (90 / increment)

    const interval = setInterval(() => {
      progress += increment
      if (progress >= 90) {
        clearInterval(interval)
        onProgress(90)
        onComplete?.()
      } else {
        onProgress(progress)
      }
    }, intervalTime)

    return () => clearInterval(interval)
  }

  const completeProgress = (onProgress: (progress: number) => void) => {
    onProgress(100)
  }

  return {
    simulateProgress,
    completeProgress
  }
}
